import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Chip,
  Box,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Swal from "sweetalert2";
import { isMobile } from "react-device-detect";
import StyledDataGrid from "components/bento/StyledDataGrid";
import { PURPLE_MAIN } from "components/bento/bentoTokens";
import { GetDepartureBatchDetail, ClearDepartureBatchItems, CancelDepartureBatch } from "api/departureBatch";
import { playCorrectSound, playIncorrectSound } from "utils";

// Item-level Clear ("housekeeping correction — a mistaken/stuck batch entry;
// package unaffected but deliberately excluded from future candidate lists,
// see backend's getDepartureCandidates) and Cancel ("the shipment itself was
// called off — package IS offered again as a candidate") share the same
// selection: default-select-all-active, staff unticks what an action
// shouldn't apply to, then picks which of the two buttons to run — see
// departureBatch/CreateTab.jsx for the same select-all-then-untick idea
// applied to creating a batch in the first place.
const BatchDetailDialog = ({ open, batchId, onClose, onChanged }) => {
  const { t } = useTranslation();
  const [batch, setBatch] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);
  const [isActing, setIsActing] = useState(false);

  const loadBatch = () => {
    if (!batchId) return;
    setIsLoading(true);
    GetDepartureBatchDetail(batchId).then((res) => {
      setBatch(res.success ? res.data : null);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    if (open && batchId) {
      loadBatch();
    } else {
      setBatch(null);
      setSelectionModel([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, batchId]);

  useEffect(() => {
    if (batch) {
      setSelectionModel(batch.items.filter((item) => !item.removedAt).map((item) => item.packageId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batch]);

  const runAction = (actionFn, confirmOpts, successTitle) => {
    if (selectionModel.length === 0) return;
    Swal.fire({
      icon: confirmOpts.icon,
      title: confirmOpts.title,
      text: confirmOpts.text,
      showCancelButton: true,
      confirmButtonText: confirmOpts.confirmButtonText,
      cancelButtonText: t("common.cancel"),
    }).then((result) => {
      if (!result.isConfirmed) return;
      setIsActing(true);
      actionFn(batchId, selectionModel).then((res) => {
        setIsActing(false);
        if (res.success) {
          playCorrectSound();
          Swal.fire({ icon: "success", title: successTitle });
          loadBatch();
          onChanged?.();
        } else {
          playIncorrectSound();
          Swal.fire({ icon: "error", title: t("departureBatch.actionErrorTitle"), text: res.message });
        }
      });
    });
  };

  const handleClear = () =>
    runAction(
      ClearDepartureBatchItems,
      {
        icon: "info",
        title: t("departureBatch.confirmClearTitle", { count: selectionModel.length }),
        text: t("departureBatch.confirmClearText"),
        confirmButtonText: t("departureBatch.clearItems", { count: selectionModel.length }),
      },
      t("departureBatch.clearSuccessTitle")
    );

  const handleCancel = () =>
    runAction(
      CancelDepartureBatch,
      {
        icon: "warning",
        title: t("departureBatch.confirmCancelTitle", { count: selectionModel.length }),
        text: t("departureBatch.confirmCancelText"),
        confirmButtonText: t("departureBatch.cancelItems", { count: selectionModel.length }),
      },
      t("departureBatch.cancelSuccessTitle")
    );

  const columns = [
    { field: "trackingSnapshot", headerName: t("departureBatch.columnTracking"), flex: 1 },
    {
      field: "status",
      headerName: t("departureBatch.historyColumnStatus"),
      flex: 1,
      renderCell: (params) => {
        const item = params.row;
        if (!item.removedAt) {
          return <Chip label={t("departureBatch.itemStatusActive")} size="small" color="secondary" />;
        }
        const label = t(
          item.removedReason === "cleared"
            ? "departureBatch.itemStatusCleared"
            : "departureBatch.itemStatusCancelled"
        );
        return <Chip label={label} size="small" color="secondary" variant="outlined" />;
      },
    },
    {
      field: "removedBy",
      headerName: t("departureBatch.historyColumnSubmittedBy"),
      flex: 1,
      valueGetter: (params) => params.row.removedBy || "",
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={isMobile}>
      <DialogTitle>
        <Typography variant="h6" color="secondary">
          {t("departureBatch.detailTitle")}
          {batch ? ` — ${batch.batchNo}` : ""}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: 0 }}>
        {batch && (
          <Box height="55vh" width="100%">
            <StyledDataGrid
              loading={isLoading}
              getRowId={(row) => row.packageId}
              rows={batch.items}
              columns={columns}
              checkboxSelection
              isRowSelectable={(params) => !params.row.removedAt}
              selectionModel={selectionModel}
              onSelectionModelChange={(model) => setSelectionModel(model)}
              containerSx={{ height: "100%", boxShadow: "none", border: "none" }}
              autoHeight={false}
              sx={{
                "& .MuiCheckbox-root.Mui-checked, & .MuiCheckbox-root.MuiCheckbox-indeterminate": {
                  color: PURPLE_MAIN,
                },
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ gap: 1 }}>
        <Button
          variant="outlined"
          color="secondary"
          disabled={!batch || selectionModel.length === 0 || isActing}
          onClick={handleClear}
        >
          {t("departureBatch.clearItems", { count: selectionModel.length })}
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={!batch || selectionModel.length === 0 || isActing}
          onClick={handleCancel}
        >
          {t("departureBatch.cancelItems", { count: selectionModel.length })}
        </Button>
        <Button variant="text" color="secondary" onClick={onClose}>
          {t("common.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BatchDetailDialog;
